import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { useHistory, useLocation } from 'react-router-dom'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-rangeslider/lib/index.css'
import 'antd/dist/reset.css'
import { HeliosWrap, createApiClient, VersionChecker } from '@oneblinktech-org/helios-base'
import { Provider as ReduxProvider, useDispatch, useSelector } from 'react-redux'
import SearchActions from './components/searchActions';
import store from './store'
import PlanningBoard from "./pages/planningBoard"
import Profile from './pages/user/Profile'
import JobList from './pages/job/JobList'
import Job from './pages/job/Job'
import Home from './pages/Home'
import Dashboard from './pages/Dashboard'
import {
  getJobByOrg
} from './store/job/actions';
import { menus as initialMenus } from './fakeData/sideBar';
import { konamiKeyEvent, removeKonamiKeyEvent } from "./helpers/globals"
import { getCookie } from "./helpers/globals"
import Connections from "./pages/Settings/Connections"
import Logo from "./components/common/logo"
import CustomerList from "./pages/Customers/CustomerList"
import JobModal from "./components/JobModal"
import { initializePusher, unbindPusher } from "./pusherManager"
import Settings from "./pages/Settings/Settings"
import { fetchPermissionsRequest } from "./store/permission/actions";
import { filterMenusByPermission } from "./helpers/menuPermission";
import ProtectedRoute from "./components/ProtectedRoute";
import Forbidden from "./pages/Forbidden";
import { PERMISSIONS } from "./constants/permissions";
import AutoLogout from "./pages/AutoLogout"
import { HeaderProvider, useHeader } from "./context/HeaderContext";
import Loader from "./components/Loader";

const apiUrl = process.env.REACT_APP_API_URL;
const { get, post } = createApiClient({ apiUrl });

export default function App(props) {
  return (
    <HeaderProvider>
      <AppRoutes {...props} />
    </HeaderProvider>
  );
}


export const AppRoutes = (props) => {
  konamiKeyEvent()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const [menus, setMenus] = useState(initialMenus);
  const initialRender = useRef(true);
  const countData = useSelector((state) => state.NoteReducer.countData);
  const user = useSelector((state) => state.UserReducer.user);
  const isNewJob = useSelector((state) => state.JobReducer.isNewJob )
  const orgId = getCookie('active_organization_id')
  const isUpdateData = useSelector((state) => state.UserReducer?.isUpdateData)
  const [activeOrgId, setActiveOrgId] = useState(() => getCookie("active_organization_id"));
  const [token, setToken] = useState(() => localStorage.getItem("h-access_token"));

  const { permissions, loadingPermissions } = useSelector(
    (state) => state.PermissionReducer
  );

  // read whatever the current page set via usePageHeader
  const { content: headerContent } = useHeader();

  useEffect(() => {
    if (loadingPermissions) return;

    const filtered = filterMenusByPermission(initialMenus, permissions || []);

    setMenus(filtered);
  }, [loadingPermissions, permissions]);
  
  useEffect(() => {
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(queryString);
    const jobId = params.get('jobId');

    let jobCard = document.querySelector('.job-item');
    if (jobId) {
      setTimeout(() => {
        handleJobCardClick(jobId);
      }, jobCard ? 200 : 3000);
    }
  }, [location, history])

  const handleJobCardClick = (jobId) => {
    if (jobId) {
      let jobCard = document.querySelector('.job-item');
      if (jobCard) {
        jobCard.click();
        dispatch({ type: "GET_CHOOSE_JOB", id: jobId, loading: true })
        dispatch({ type: 'SET_IS_NEW_JOB', payload: false })
      }
    }
  };

  useEffect(() => {
    localStorage.removeItem("pusher-active");
  },[])

  useEffect(() => {
    const t = setInterval(() => {
      setToken(localStorage.getItem("h-access_token"));
      setActiveOrgId(getCookie("active_organization_id"));
    }, 300);

    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!token || !activeOrgId) return;
    dispatch(fetchPermissionsRequest());
  }, [dispatch, token, activeOrgId]);

  
  useEffect(() => {
    if (Object.keys(user?.data || {}).length > 0) {
      dispatch({ type: "GET_COUNTER_DATA" });
    }
  }, [user?.data])

  useEffect(() => {
    if(initialRender.current) {
      initialRender.current = false;
    } else {
      updateCounter(countData)
    }
  }, [countData])

  const updateCounter = useCallback((result) => {
    if (!result) return;

    setMenus((prevMenus) =>
      prevMenus.map((menu) => {
        if (menu.name === "planning") {
          return {
            ...menu,
            count: result?.planning_count || 0,
          };
        }
        return menu;
      })
    );
  }, []);

  return (
    <div className="App">
      {process.env.NODE_ENV === "production" && <VersionChecker />}
      <Loader loading={loadingPermissions} />

      <Switch>
        {/* ====== Layout 1: Jobs/Settings area ====== */}
        <Route
          path={[
            "/jobs",
            "/job/edit/:id",
            "/profile",
            "/connections",
            "/customers",
            "/settings",
            "/logout"
          ]}
        >
          <HeliosWrap
            captchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
            googleClientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            fullWidth={false}
            // orgChangeEvent={(orgId) => dispatch(getJobByOrg(orgId))}
            orgChangeEvent={(newOrgId) => {
              dispatch(getJobByOrg(newOrgId));
              dispatch(fetchPermissionsRequest()); // ✅ permissions for that org
            }}
            siteLogo={<Logo />}
            siteText="Helios"
            app="helios"
            searchComponent={<SearchActions />}
            isWelcome={false}
            leftSidebarItems={menus}
            apiUrl={process.env.REACT_APP_API_URL}
            isNavShow={process.env.REACT_APP_SHOW_NAV_SIDE || true}
            isUpdateData={isUpdateData}
            headerContent={headerContent}
            subscriptionCheck={process.env.REACT_APP_SUBSCRIPTION_CHECK || false}
          >
            <ReduxProvider store={store}>
              <Switch>
                <ProtectedRoute
                  path="/jobs"
                  permission={PERMISSIONS.JOB_LIST_VIEW}
                  component={JobList}
                />

                <ProtectedRoute
                  path="/job/edit/:id"
                  permission={PERMISSIONS.JOB_EDIT}
                  component={Job}
                />

                <ProtectedRoute path="/profile" component={Profile} />

                <ProtectedRoute
                  path="/settings"
                  permission={PERMISSIONS.SETTINGS_VIEW}
                  component={Settings}
                />

                <ProtectedRoute
                  path="/connections"
                  permission={PERMISSIONS.CONNECTION_VIEW}
                  component={Connections}
                />

                <ProtectedRoute
                  path="/customers"
                  permission={PERMISSIONS.CUSTOMER_LIST_VIEW}
                  component={CustomerList}
                />

                 <Route path="/logout" render={() => <AutoLogout />} />
              </Switch>
            </ReduxProvider>
          </HeliosWrap>
        </Route>

        {/* ====== Layout 2: Planning/Production/Dashboard area ====== */}
        <Route path={["/planning", "/production", "/dashboard", "/403", "/"]}>
          <HeliosWrap
            captchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
            googleClientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            fullWidth={true}
            // orgChangeEvent={(orgId) => dispatch(getJobByOrg(orgId))}
            orgChangeEvent={(newOrgId) => {
              dispatch(getJobByOrg(newOrgId));
              dispatch(fetchPermissionsRequest()); // ✅ permissions for that org
            }}
            leftSidebarItems={menus}
            siteLogo={<Logo />}
            siteText="Helios"
            app="helios"
            searchComponent={<SearchActions />}
            isWelcome={false}
            apiUrl={process.env.REACT_APP_API_URL}
            isNavShow={process.env.REACT_APP_SHOW_NAV_SIDE || true}
            isUpdateData={isUpdateData}
            headerContent={headerContent}
            subscriptionCheck={process.env.REACT_APP_SUBSCRIPTION_CHECK || false}
          >
            <ReduxProvider store={store}>
              <Switch>
                <Route path="/403" component={Forbidden} />

                <ProtectedRoute
                  path="/planning"
                  permission={PERMISSIONS.PLANNING_BOARD_VIEW}
                  component={PlanningBoard}
                />

                <ProtectedRoute
                  path="/production"
                  permission={PERMISSIONS.PRODUCTION_BOARD_VIEW}
                  component={Home}
                />

                <ProtectedRoute
                  path="/dashboard"
                  permission={PERMISSIONS.DASHBOARD_VIEW}
                  component={Dashboard}
                />

                {/* ✅ IMPORTANT: exact "/" so it doesn't match /403, /dashboard etc */}
                <Route
                  exact
                  path="/"
                  render={() => {
                    if (loadingPermissions) return null; // or a loader component

                    if (permissions.includes(PERMISSIONS.PLANNING_BOARD_VIEW)) {
                      return <Redirect to="/planning" />;
                    }
                    if (permissions.includes(PERMISSIONS.DASHBOARD_VIEW)) {
                      return <Redirect to="/dashboard" />;
                    }
                    if (permissions.includes(PERMISSIONS.PRODUCTION_BOARD_VIEW)) {
                      return <Redirect to="/production" />;
                    }
                    return <Redirect to="/403" />;
                  }}
                />
              </Switch>
            </ReduxProvider>
          </HeliosWrap>
        </Route>
      </Switch>

      {orgId &&
        <JobModal />
      }
    </div>
  );
}
